import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../../styles/UpgradePromptModal.css';

/**
 * UpgradePromptModal Component
 *
 * Modal shown when user attempts an action that exceeds their plan limits.
 * Prompts user to upgrade their subscription.
 */
const UpgradePromptModal = ({
    isOpen,
    onClose,
    currentDogs,
    maxDogs,
    planName,
    suggestedPlanName,
    availableUpgrades
}) => {
    const { t } = useTranslation(['subscription', 'common']);
    const navigate = useNavigate();

    if (!isOpen) return null;

    const handleUpgradeClick = () => {
        onClose();
        navigate('/subscription/plans');
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="upgrade-modal-overlay" onClick={handleOverlayClick}>
            <div className="upgrade-modal">
                <div className="upgrade-modal-header">
                    <div className="upgrade-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h2>{t('upgrade.dogLimitTitle')}</h2>
                </div>

                <div className="upgrade-modal-body">
                    <p className="limit-info">
                        {t('upgrade.planLimitInfo', { plan: planName, max: maxDogs })}
                    </p>
                    <p className="current-usage">
                        {t('upgrade.currentUsage', { current: currentDogs, max: maxDogs })}
                    </p>

                    <div className="upgrade-suggestion">
                        <p>{t('upgrade.upgradeMessage')}</p>
                        {suggestedPlanName && (
                            <p className="suggested-plan">
                                {t('upgrade.recommendedPlan')} <strong>{suggestedPlanName}</strong>
                            </p>
                        )}
                    </div>

                    {availableUpgrades && availableUpgrades.length > 0 && (
                        <div className="available-upgrades">
                            <p className="upgrades-title">{t('upgrade.availablePlansTitle')}</p>
                            <ul>
                                {availableUpgrades.map((plan) => (
                                    <li key={plan.code}>
                                        <span className="plan-upgrade-name">{plan.name}</span>
                                        <span className="plan-upgrade-details">
                                            {plan.maxDogs ? t('upgrade.upToDogs', { max: plan.maxDogs }) : t('upgrade.unlimitedDogs')} - {plan.monthlyPrice}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="upgrade-modal-footer">
                    <button className="btn-secondary" onClick={onClose}>
                        {t('common:cancel')}
                    </button>
                    <button className="btn-primary" onClick={handleUpgradeClick}>
                        {t('upgrade.viewPlans')}
                    </button>
                </div>
            </div>
        </div>
    );
};

UpgradePromptModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    currentDogs: PropTypes.number.isRequired,
    maxDogs: PropTypes.number.isRequired,
    planName: PropTypes.string.isRequired,
    suggestedPlanName: PropTypes.string,
    availableUpgrades: PropTypes.arrayOf(
        PropTypes.shape({
            code: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            maxDogs: PropTypes.number,
            monthlyPrice: PropTypes.string
        })
    )
};

UpgradePromptModal.defaultProps = {
    suggestedPlanName: null,
    availableUpgrades: null
};

export default UpgradePromptModal;
